import type { MonsterAppearance } from "../render/MonsterModel";
import { Combatant, type Stats } from "./Stats";

export interface MonsterSkillUse {
  skill: string;
  /** 0..1 chance of being chosen on a given turn. */
  chance: number;
  /** Only considered when the monster is below this HP fraction. */
  whenHpBelow?: number;
}

export interface MonsterDef {
  id: string;
  name: string;
  appearance: MonsterAppearance;
  stats: Stats;
  exp: number;
  gold: number | [number, number];
  skills?: MonsterSkillUse[];
  /** 0..1 chance of trying to run away each turn once badly hurt. */
  cowardice?: number;
  description?: string;
}

/** A monster instance in a battle. Several may share one definition. */
export class Monster extends Combatant {
  readonly def: MonsterDef;
  /** "A", "B", "C"… appended when duplicates are present. */
  suffix = "";
  /** Set when the monster has run away; it is out of the fight but not dead. */
  fled = false;

  constructor(def: MonsterDef) {
    super();
    this.def = def;
    this.hp = def.stats.maxHp;
    this.mp = def.stats.maxMp;
  }

  get name(): string {
    return this.suffix ? `${this.def.name} ${this.suffix}` : this.def.name;
  }

  get stats(): Stats {
    return this.def.stats;
  }

  get active(): boolean {
    return this.alive && !this.fled;
  }
}

/** Appends A/B/C to duplicate names so targeting is unambiguous. */
export function labelDuplicates(monsters: Monster[]): void {
  const counts = new Map<string, number>();
  for (const m of monsters) counts.set(m.def.id, (counts.get(m.def.id) ?? 0) + 1);

  const seen = new Map<string, number>();
  for (const m of monsters) {
    if ((counts.get(m.def.id) ?? 0) < 2) continue;
    const index = seen.get(m.def.id) ?? 0;
    seen.set(m.def.id, index + 1);
    m.suffix = String.fromCharCode(65 + index);
  }
}
